import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Leaf, Flame } from "lucide-react";
import SEO from "../../components/SEO";
import { MEDIA, REGISTRATION_LINKS } from "../../lib/constants";

export default function KhalifaKushDC() {
  return (
    <>
      <SEO
        title="Khalifa Kush in DC: Where to Find It | District Wellness Center"
        description="Khalifa Kush — Wiz Khalifa's namesake OG Kush strain — in stock at District Wellness Center on Georgia Ave NW. Effects, terpene profile, pre-rolls & Khalifa Mints flower for DC medical patients."
        path="/blog/khalifa-kush-dc-dispensary"
        ogType="article"
        ogImage={MEDIA.khalifaKush}
        breadcrumbs={[
          { name: "Blog", path: "/blog" },
          { name: "Khalifa Kush in DC", path: "/blog/khalifa-kush-dc-dispensary" },
        ]}
        article={{
          publishedTime: "2026-09-15",
          modifiedTime: "2026-09-15",
          section: "Strain Spotlight",
          image: MEDIA.khalifaKush,
        }}
      />

      {/* HERO */}
      <section className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">
              Strain Spotlight · Featured Flower
            </div>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              <em className="text-brand">Khalifa Kush</em> in Washington, DC.
            </h1>
            <p className="mt-6 text-ink-muted text-base sm:text-lg max-w-xl leading-relaxed">
              Wiz Khalifa&rsquo;s namesake strain — a legendary OG Kush cut — is
              on the District Wellness Center menu right now. Here&rsquo;s the
              short, honest breakdown DC patients actually want: what it is,
              how it hits, and which format is right for you.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
                data-testid="kk-cta-menu"
              >
                Shop Khalifa Kush <ArrowUpRight size={14} />
              </Link>
              <a
                href={REGISTRATION_LINKS.medical}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-ink/15 text-ink hover:border-brand hover:text-brand px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
                data-testid="kk-cta-register"
              >
                Register for Medical Cannabis <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <img
              src={MEDIA.khalifaKush}
              alt="Khalifa Kush cannabis pre-roll and Khalifa Mints flower jar at District Wellness Center, Washington DC"
              className="w-full h-[520px] object-cover border border-black/10"
              loading="lazy"
              data-testid="kk-hero-image"
            />
          </div>
        </div>
      </section>

      {/* ARTICLE */}
      <article className="py-20 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 font-sans text-ink-muted">
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-0 mb-5">
            What is Khalifa Kush?
          </h2>
          <p className="text-lg leading-relaxed">
            Khalifa Kush — often shortened to <strong>KK</strong> — is a phenotype
            of <em>OG Kush</em> selected by rapper and cannabis entrepreneur
            Wiz Khalifa. What started as a private cut grown for personal use
            in the late 2000s has become one of the most recognizable strain
            brands in legal cannabis. If you&rsquo;ve seen a black-and-yellow
            jar or a pre-roll with the signature script logo, that&rsquo;s the
            line.
          </p>
          <p className="text-lg leading-relaxed mt-4">
            At District Wellness Center, we carry Khalifa Kush in two formats:
            the <strong>Khalifa Mints cannabis flower jar</strong> and the
            <strong> Khalifa Kush cannabis pre-roll</strong>. Both are lab-tested,
            DC-compliant, and drop into inventory in small batches.
          </p>

          {/* Effects */}
          <div className="mt-12 grid sm:grid-cols-3 gap-4">
            <div className="p-6 bg-white border border-black/10">
              <Sparkles size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Type</div>
              <div className="font-serif text-xl text-ink">Indica-leaning hybrid</div>
            </div>
            <div className="p-6 bg-white border border-black/10">
              <Leaf size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Lineage</div>
              <div className="font-serif text-xl text-ink">OG Kush phenotype</div>
            </div>
            <div className="p-6 bg-white border border-black/10">
              <Flame size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Vibe</div>
              <div className="font-serif text-xl text-ink">Piney · earthy · heavy</div>
            </div>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            How Khalifa Kush actually feels
          </h2>
          <p className="text-lg leading-relaxed">
            KK is a slow, warm high. Most DC patients describe the first 20
            minutes as clear and euphoric — good for winding down after work,
            not for tackling a spreadsheet. It settles into a heavier body
            relaxation, which is why the strain has a loyal following among
            patients who use cannabis for muscle tension, nighttime pain, and
            sleep support.
          </p>
          <p className="text-lg leading-relaxed mt-4">
            The terpene profile leans <strong>pine, earthy Kush, and a light
            citrus lift</strong> — dominated by limonene, myrcene, and
            beta-caryophyllene. That last one, caryophyllene, is a big reason
            Khalifa Kush shows up on so many patients&rsquo; short lists: it
            binds to CB2 receptors and is widely associated with anti-
            inflammatory and calming effects.
          </p>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Flower jar vs. pre-roll — which should you pick?
          </h2>
          <ul className="space-y-4">
            <li className="flex gap-3 text-lg">
              <span className="text-brand font-serif text-xl leading-none">✦</span>
              <span>
                <strong>Khalifa Mints flower jar</strong> — best if you have
                your own rolling setup or a dry-herb vape. You get the full
                nose, control your dose, and the jar lasts longer per dollar.
              </span>
            </li>
            <li className="flex gap-3 text-lg">
              <span className="text-brand font-serif text-xl leading-none">✦</span>
              <span>
                <strong>Khalifa Kush pre-roll</strong> — best if you&rsquo;re
                new, in a hurry, or want a discreet single-session option. The
                packaging keeps it fresh and it&rsquo;s ready to spark.
              </span>
            </li>
          </ul>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Is Khalifa Kush good for beginners?
          </h2>
          <p className="text-lg leading-relaxed">
            Honestly? Only if you go slow. KK regularly tests in the 22–28%
            THC range. If you&rsquo;re new to flower, take one or two draws,
            wait 10–15 minutes, and reassess. Our patient specialists will
            walk you through this at the counter — no pressure, no jargon.
          </p>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Where to buy Khalifa Kush in Washington, DC
          </h2>
          <p className="text-lg leading-relaxed">
            You&rsquo;ll find Khalifa Kush at{" "}
            <strong>District Wellness Center — 4915 Georgia Ave NW</strong>,
            our flagship dispensary in Petworth. We restock in small batches;
            when it lands, it moves fast. The easiest way to know if it&rsquo;s
            live on the menu is to check our live Flowhub-synced inventory:
          </p>
          <div className="mt-6">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 bg-ink hover:bg-brand text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
              data-testid="kk-menu-check"
            >
              Check the live menu <ArrowUpRight size={14} />
            </Link>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Not registered yet? It takes ~5 minutes.
          </h2>
          <p className="text-lg leading-relaxed">
            To purchase any medical cannabis in DC — Khalifa Kush included —
            you need to be 21+ and registered through the myABCA portal. It
            takes a few minutes online, no doctor visit required.
          </p>
          <div className="mt-6">
            <a
              href={REGISTRATION_LINKS.medical}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
              data-testid="kk-register-cta"
            >
              Register for Medical Cannabis <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </article>

      {/* FINAL CTA */}
      <section className="bg-ink text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">
            Come see Khalifa Kush in person.
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            4915 Georgia Ave NW — open 7 days. Ask any patient specialist for
            the current KK drop and they&rsquo;ll pull the jar so you can
            smell it before you buy.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em]"
              data-testid="kk-final-menu"
            >
              Shop the live menu <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/locations"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-brand hover:text-brand text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em]"
              data-testid="kk-final-locations"
            >
              Directions & hours
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
